/**
 * Species and breed reference data for every surface that offers one.
 *
 * The 2,710-breed master lives in `public/species-breeds.json` and is FETCHED, not
 * bundled. It is 40 KB and only the schedule edit dialog needs it, so shipping it in
 * JS would tax every page for one screen. Regenerate it with
 * `node scripts/generate-species-breeds.mjs <master.csv>`.
 *
 * What ends up here reaches the modality: the schedule form writes
 * schedule_item.patient_species / patient_breed, and the Gateway emits those verbatim
 * as PatientSpeciesDescription (0010,2201) and PatientBreedDescription (0010,2292) on
 * the DICOM worklist. Free text stays allowed, because a breed nobody has heard of is
 * still better than a blank field.
 *
 * This module is the single source of truth for the offered species and breeds. The
 * teleconsultation submit dialog used to carry its own 2,730-line bundled copy; it was
 * a strict subset of this one and has been retired. Anything genuinely missing belongs
 * in the curated CSV and a regeneration, never hardcoded into a component.
 */
/**
 * Species offered in the picker. Companion species first because that is the bulk of
 * the caseload, then production and lab species, then the catch-alls.
 *
 * 'Other' has no breeds of its own and is deliberately not in the master: it is the
 * escape hatch for a species the list does not cover, where the breed field falls back
 * to free text. `speciesBreeds.test.ts` asserts every OTHER species here has breeds in
 * the JSON, and that the JSON has no species the picker cannot offer.
 */
export const SPECIES = [
    'Canine',
    'Feline',
    'Equine',
    'Avian',
    'Bovine',
    'Caprine',
    'Ovine',
    'Porcine',
    'Lagomorph',
    'Exotic',
    'Other',
];
/** Species with no breed list of their own; the breed field is free text. */
export const SPECIES_WITHOUT_BREEDS = ['Other'];
/**
 * Letters that NFKD leaves intact because they are distinct letters rather than a base
 * plus a combining mark. Without these, "Dolafe" never matches "Dølafe" and "Spaelsau"
 * never matches "Spælsau", which is not a hypothetical: the master carries Nordic
 * cattle and sheep breeds.
 */
const LETTER_FOLDING = {
    ø: 'o',
    æ: 'ae',
    œ: 'oe',
    ß: 'ss',
    đ: 'd',
    ð: 'd',
    þ: 'th',
    ł: 'l',
    ı: 'i',
};
/**
 * Fold a name to a comparison key: accents removed, case dropped, everything that is
 * not a letter or digit discarded. So "Albères", "alberes" and "ALBERES" all match, and
 * so do "Reptile - Lizard" and "Reptile Lizard".
 */
export function foldForSearch(value) {
    let out = '';
    for (const ch of value.toLowerCase())
        out += LETTER_FOLDING[ch] ?? ch;
    return out
        .normalize('NFKD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^\p{L}\p{N}]/gu, '');
}
/**
 * Resolve a free-form species string to the key SPECIES and the master are indexed by.
 *
 * DICOM sends species however the sending modality felt like spelling it, so a study
 * routinely arrives as 'canine' or 'FELINE' while everything here is title case. The
 * index lookups (`searchBreeds`, `hasBreedList`) are deliberately case-SENSITIVE, so a
 * raw DICOM value has to come through here first or the breed list silently comes back
 * empty and the field reads as "this species has no breeds".
 *
 * Resolution is against the static SPECIES list rather than the fetched index, so it
 * works before the master lands and it covers 'Other', which has no breeds by design.
 * Returns null for a blank string or a species the picker does not offer.
 */
export function resolveSpeciesKey(species) {
    const q = foldForSearch(species);
    if (!q)
        return null;
    return SPECIES.find((s) => foldForSearch(s) === q) ?? null;
}
let pending = null;
/**
 * Fetch the master once per page load and memoize it. A failure resolves to an empty
 * index rather than rejecting: the breed field is free text, so a missing suggestion
 * list degrades the form, it does not break it.
 */
export function loadSpeciesBreeds() {
    if (!pending) {
        pending = fetch('/species-breeds.json')
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
            .then((data) => data && typeof data === 'object' && data.breeds ? data : { breeds: {} })
            .catch(() => ({ breeds: {} }));
    }
    return pending;
}
/** Test seam: drop the memoized fetch so each test starts clean. */
export function resetSpeciesBreedsCache() {
    pending = null;
}
/**
 * Pass as `searchBreeds`' limit to ask for every match instead of a screenful.
 *
 * The default cap of 50 is right for a dropdown the user scrolls; it is wrong for the
 * teleconsultation submit dialog, whose breed popover has always rendered the whole
 * species list and whose test says so. Opting out explicitly beats a magic number, and
 * keeps the default (which the schedule form's cap hint depends on) untouched.
 */
export const ALL_BREEDS = Number.POSITIVE_INFINITY;
/**
 * How many breed suggestions a picker renders at once.
 *
 * The master is 2,710 breeds and the tail is long: Bovine alone has 821 and
 * Porcine 448. Mounting that many Radix SelectItems inside an already-heavy
 * dialog is visibly janky, and nobody scrolls 821 rows anyway; past the first
 * screenful the search box is the faster path. 50 fills the dropdown several
 * times over, so the list never looks truncated at a glance, and the count
 * hint below it tells the user to keep typing when it is.
 */
export const BREED_SUGGESTION_LIMIT = 50;
/**
 * Suggest breeds for a species, ranked prefix matches first then substring matches,
 * each alphabetically. An empty query returns the head of the list so the field is
 * useful before the user types.
 *
 * Bovine alone has 821 entries, so callers should cap with `limit`.
 */
export function searchBreeds(index, species, query, limit = 50) {
    const list = index?.breeds?.[species];
    if (!list?.length)
        return [];
    const q = foldForSearch(query);
    if (!q)
        return list.slice(0, limit);
    const prefix = [];
    const contains = [];
    for (const breed of list) {
        const folded = foldForSearch(breed);
        if (folded.startsWith(q))
            prefix.push(breed);
        else if (folded.includes(q))
            contains.push(breed);
        if (prefix.length >= limit)
            break;
    }
    return [...prefix, ...contains].slice(0, limit);
}
/** True when the species is one the picker offers a breed list for. */
export function hasBreedList(index, species) {
    return Boolean(index?.breeds?.[species]?.length);
}
